// frontend/src/components/wizard/ConnectionComponents.jsx
import React, { useState } from 'react';
import {
  Database, FileSpreadsheet, Server, Cloud, Briefcase,
  DownloadCloud, UploadCloud, Search, ChevronDown, ChevronUp, CheckCircle2,
  Eye, EyeOff, AlertCircle, ShieldCheck, Link as LinkIcon, Copy, XCircle,
  FileText
} from 'lucide-react';
import { generateDbaRequest } from '../../utils/dbaRequestGenerator';

import { useLanguageStore } from '../../store/useLanguageStore'; // 🟢 Importamos el store

import { DATIAFLOW_IP, DATIAFLOW_IP_CIDR, firewallProviders, getProvidersList }
  from '../../utils/firewallInstructions';

const ICON_MAP = {
  'Database': Database, 'Server': Server, 'FileSpreadsheet': FileSpreadsheet,
  'Cloud': Cloud, 'Briefcase': Briefcase, 'DownloadCloud': DownloadCloud,
  'UploadCloud': UploadCloud
};

// ==========================================
// 1. SELECTOR DE MOTORES (GRILLA)
// ==========================================
export const EngineSelector = ({
  categories,
  searchEngine,
  setSearchEngine,
  expandedCats,
  toggleCategory,
  register,
  selectedEngine,
  setValue
}) => {
  const { t } = useLanguageStore();

  // 🟢 Filtro robusto: con fallback por si la llave aún no llega del backend
  const filteredCategories = categories.map(cat => ({
    ...cat,
    options: cat.options.filter(opt => {
      // Intentamos traducir, si no existe la llave, usamos el label original (fallback seguro)
      const translatedName = t(`wizard.engines.${opt.engine_key}`) || opt.label || "";
      return translatedName.toLowerCase().includes((searchEngine || "").toLowerCase());
    })
  })).filter(cat => cat.options.length > 0);

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <label className="text-sm font-bold text-slate-900">{t('wizard.connection.select_engine')}</label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={t('wizard.connection.search_placeholder')}
            value={searchEngine}
            onChange={(e) => setSearchEngine(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-600 outline-none w-full sm:w-64"
          />
        </div>
      </div>
      <div className="space-y-4">
        {filteredCategories.map((category) => {
          // const isExpanded = searchEngine.length > 0 || expandedCats.includes(category.category_key || category.title);
          const isExpanded = searchEngine.length > 0
            || expandedCats.includes(category.category_key || category.title)
            || category.options.some(opt => (opt.engine_key || opt.id) === selectedEngine);
          return (
            <div key={category.category_key} className="border border-slate-100 rounded-xl overflow-hidden">
              <button type="button" onClick={() => toggleCategory(category.category_key)} className="w-full flex items-center justify-between px-4 py-2 bg-slate-50 hover:bg-slate-100 transition-colors">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                  {/* 🟢 TRADUCCIÓN DE CATEGORÍA */}
                  {t(`wizard.categories.${category.category_key}`)}
                  <span className="ml-2 bg-slate-200 px-2 py-0.5 rounded-full text-[10px]">{category.options.length}</span>
                </h3>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {isExpanded && (
                <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white">
                  {category.options.map((engine) => {
                    const Icon = ICON_MAP[engine.icon] || Database;
                    // 🟢 EL FIX: Usamos engine_key (ej: 'mysql') como el valor real que necesita el formulario
                    const engineValue = engine.engine_key || engine.id;
                    const isSelected = selectedEngine === engineValue

                    // 🟢 LA MAGIA DE LOS DEFAULTS ESTÁ AQUÍ
                    const handleEngineChange = () => {
                      // Solo aplicamos defaults si está CAMBIANDO de motor.
                      // Si ya estaba en MySQL, no hacemos nada para respetar sus cambios manuales.
                      if (selectedEngine !== engineValue && setValue) {
                        const config = engine.config_default || {};

                        // Seteamos el puerto o lo limpiamos si el nuevo motor no usa
                        setValue('port', config.default_port || '', { shouldValidate: true });

                        // Seteamos el usuario por defecto o lo limpiamos
                        setValue('user', config.default_user || '', { shouldValidate: true });
                      }
                    };
                    return (
                      <label
                        key={engine.id}
                        className={`relative flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${isSelected ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white text-slate-600 hover:bg-slate-50'}`}
                      >
                        {/* 🟢 Pasamos engineValue al value del input */}
                        <input
                          type="radio"
                          value={engineValue}
                          className="sr-only"
                          {...register('engineType')}
                          onClick={handleEngineChange} // 🟢 INTERCEPTAMOS EL CLIC
                        />
                        <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-indigo-600' : 'text-slate-400'}`} />

                        {/* 🟢 Fallback para el motor */}
                        <span className="text-xs sm:text-sm font-medium text-center">
                          {t(`wizard.engines.${engine.engine_key}`) || engine.label}
                        </span>

                        {isSelected && <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-indigo-600" />}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// 2. FORMULARIO DE BASE DE DATOS SQL
// ==========================================
export const DatabaseForm = ({
  register, errors, showPassword, setShowPassword, isTarget, onCopyFromSource,
  networkType, onNetworkTypeChange, watch,
  connectionMethod, onConnectionMethodChange, onTrimField,
  isApiEngine = false,   // 🆕 motores API (Odoo): ocultar sección de red + etiqueta URL
}) => {
  const { t } = useLanguageStore();
  // ✅ NO crear estados locales - usar props del parent (networkType, connectionMethod)
  // Estos vienen del watch() del formulario en StepSource/StepTarget

  const engineType = watch?.('engineType') || '';
  const authType = watch?.('auth_type') || 'none';
  const paginationType = watch?.('pagination_type') || 'auto';
  const [showAdvancedRest, setShowAdvancedRest] = useState(false);

  // const [networkType, setNetworkType] = useState('cloud');
  // const [useSsh, setUseSsh] = useState(false);
  // const [connectionMethod, setConnectionMethod] = useState('firewall');

  return (
    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4 animate-in fade-in">

      {/* SECCIÓN 1: Detección de Red Local — oculta para motores API (Odoo usa HTTP, no SSH/firewall TCP) */}
      {!isApiEngine && engineType !== 'bigquery' && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5 mb-6 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-bold text-blue-900 text-sm mb-3">
                {t('wizard.connection.where_is_db') || '¿Dónde está tu base de datos? ¿Es pública o privada?'}
              </h4>
              <div className="space-y-2">
                <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-blue-100"
                  style={{ borderColor: networkType === 'cloud' ? '#0EA5E9' : '#E2E8F0' }}>
                  <input
                    type="radio"
                    checked={networkType === 'cloud'}
                    onChange={() => {
                      onNetworkTypeChange?.('cloud');
                      // setNetworkType('cloud');
                      // setUseSsh(false);
                    }}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="ml-3 text-sm font-medium text-slate-700">
                    ☁️ {t('wizard.connection.cloud_public') || 'En la nube - Pública (acceso directo)'}
                  </span>
                </label>

                <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-blue-100"
                  style={{ borderColor: networkType === 'local' ? '#0EA5E9' : '#E2E8F0' }}>
                  <input
                    type="radio"
                    checked={networkType === 'local'}
                    // onChange={() => setNetworkType('local')}
                    onChange={() => onNetworkTypeChange?.('local')}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="ml-3 text-sm font-medium text-slate-700">
                    🔒 {t('wizard.connection.local_private') || 'Privada (Firewall, SSH o red local)'}
                  </span>
                </label>
              </div>

              {networkType === 'local' && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-blue-100 text-xs text-slate-600">
                  <p className="mb-2">
                    {t('wizard.connection.local_notice') || 'Necesitarás elegir una de estas opciones:'}
                  </p>
                  <ul className="space-y-1 ml-4">
                    <li>✓ {t('wizard.connection.option_firewall') || 'Abrir tu firewall a nuestra IP'}</li>
                    <li>✓ {t('wizard.connection.option_ssh') || 'Usar SSH Tunnel (más seguro)'}</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SECCIÓN 2: Elegir método si es red local (no aplica a motores API) */}
      {!isApiEngine && engineType !== 'bigquery' && networkType === 'local' && !isTarget && (
        <div className="border-t border-slate-200 pt-4">
          <p className="text-sm font-bold text-slate-700 mb-3">
            {t('wizard.connection.choose_method') || '¿Cómo conectar a tu BD local?'}
          </p>
          <div className="space-y-3">
            {/* Opción SSH - RADIO BUTTON */}
            <label className="flex items-start p-4 border-2 border-slate-200 rounded-xl cursor-pointer hover:border-indigo-300 hover:bg-indigo-50 transition-all"
              style={{ borderColor: connectionMethod === 'ssh' ? '#4F46E5' : 'inherit', backgroundColor: connectionMethod === 'ssh' ? '#EEF2FF' : 'inherit' }}>
              <input
                type="radio"
                name="connectionMethod"
                value="ssh"
                checked={connectionMethod === 'ssh'}
                // onChange={(e) => setConnectionMethod(e.target.value)}
                onChange={(e) => {
                  onConnectionMethodChange?.('ssh');
                }}
                className="w-4 h-4 cursor-pointer mt-0.5"
              />
              <div className="ml-3">
                <span className="text-sm font-bold text-slate-900 block">
                  🔐 {t('wizard.connection.use_ssh') || 'Usar SSH Tunnel'}
                </span>
                <span className="text-xs text-slate-600 block mt-1">
                  {t('wizard.connection.ssh_recommended') || 'Recomendado. Conexión segura, sin abrir firewall'}
                </span>
              </div>
            </label>

            {/* Opción Firewall - RADIO BUTTON */}
            <label className="flex items-start p-4 border-2 border-slate-200 rounded-xl cursor-pointer hover:border-amber-300 hover:bg-amber-50 transition-all"
              style={{ borderColor: connectionMethod === 'firewall' ? '#B45309' : 'inherit', backgroundColor: connectionMethod === 'firewall' ? '#FFFBEB' : 'inherit' }}>
              <input
                type="radio"
                name="connectionMethod"
                value="firewall"
                checked={connectionMethod === 'firewall'}
                // onChange={(e) => setConnectionMethod(e.target.value)}
                onChange={(e) => {
                  onConnectionMethodChange?.('firewall');
                }}
                className="w-4 h-4 cursor-pointer mt-0.5"
              />
              <div className="ml-3">
                <span className="text-sm font-bold text-slate-900 block">
                  🔓 {t('wizard.connection.open_firewall') || 'Abrir Firewall'}
                </span>
                <span className="text-xs text-slate-600 block mt-1">
                  {t('wizard.connection.firewall_simple') || 'Abre tu firewall a nuestra IP'}
                </span>
              </div>
            </label>

            {/* Opción Conexión directa - RADIO BUTTON */}
            <label className="flex items-start p-4 border-2 border-slate-200 rounded-xl cursor-pointer hover:border-emerald-300 hover:bg-emerald-50 transition-all"
              style={{ borderColor: connectionMethod === 'direct' ? '#059669' : 'inherit', backgroundColor: connectionMethod === 'direct' ? '#F0FDF4' : 'inherit' }}>
              <input
                type="radio"
                name="connectionMethod"
                value="direct"
                checked={connectionMethod === 'direct'}
                // onChange={(e) => setConnectionMethod(e.target.value)}
                // onChange={(e) => {
                //   setConnectionMethod(e.target.value);
                //   onConnectionMethodChange?.(e.target.value);  // 🆕 Notificar al parent
                // }}
                onChange={(e) => {
                  onConnectionMethodChange?.('direct');
                }}
                className="w-4 h-4 cursor-pointer mt-0.5"
              />
              <div className="ml-3">
                <span className="text-sm font-bold text-slate-900 block">
                  🔌 {t('wizard.connection.direct_access') || 'Conexión directa'}
                </span>
                <span className="text-xs text-slate-600 block mt-1">
                  {t('wizard.connection.direct_desc') || 'Sin firewall ni SSH. Para redes internas accesibles.'}
                </span>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* SECCIÓN 3: Campos de BD estándar */}
      {!['bigquery', 'hubspot'].includes(engineType) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-1">
              <label className="text-sm font-medium text-slate-700 block">
                {engineType === 'rest_api'
                  ? t('wizard.forms.base_url')
                  : engineType === 'dynamics365'
                    ? t('wizard.forms.org_url')
                    : ['zoho_crm', 'zoho_books', 'zoho_inventory'].includes(engineType)
                      ? t('wizard.forms.zoho_domain')
                      : ['sugarcrm', 'suitecrm'].includes(engineType)
                        ? t('wizard.forms.instance_url')
                        : engineType === 'netsuite'
                          ? t('wizard.forms.account_id')
                          : engineType === 'defontana'
                            ? (t('wizard.forms.environment') || 'Ambiente')
                            : isApiEngine
                              ? t('wizard.forms.api_url')
                              : engineType === 'snowflake'
                                ? t('wizard.forms.account_url')
                                : t('wizard.forms.host')}
              </label>
              {isTarget && (
                <button type="button" onClick={onCopyFromSource} className="text-[11px] text-emerald-700 bg-emerald-100/50 hover:bg-emerald-100 px-2 py-1 rounded transition font-medium">
                  {t('wizard.forms.copy_source')}
                </button>
              )}
            </div>
            <input
              {...register('host')}
              autoComplete="off"
              // placeholder={useSsh ? "localhost" : "192.168.1.50"}
              type="text"
              onBlur={(e) => onTrimField?.('host', e.target.value)}
              // placeholder={isApiEngine
              //   ? (engineType === 'salesforce'
              //     ? "https://midominio.my.salesforce.com"
              //     : "https://miempresa.odoo.com")
              //   : (connectionMethod === 'ssh' ? "localhost" : "192.168.1.50")}
              placeholder={engineType === 'rest_api'
                ? "https://api.ejemplo.com"
                : engineType === 'dynamics365'
                  ? "https://miorg.crm.dynamics.com"
                  : ['zoho_crm', 'zoho_books', 'zoho_inventory'].includes(engineType)
                    ? "https://accounts.zoho.com"
                    : ['sugarcrm', 'suitecrm'].includes(engineType)
                      ? "https://mycompany.sugarcrm.com"
                      : engineType === 'netsuite'
                        ? "1234567 o TSTDRV1234567"
                        : engineType === 'defontana'
                          ? "production"
                          : isApiEngine
                          ? (engineType === 'salesforce'
                            ? "https://midominio.my.salesforce.com"
                            : "https://miempresa.odoo.com")
                          : engineType === 'snowflake'
                            ? "xy12345-AB43234 (NO .snowflakecomputing.com)"
                            : (connectionMethod === 'ssh' ? "localhost" : "192.168.1.50")}
              className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.host ? 'border-red-500' : 'border-slate-200'}`}
            />
            {errors.host && <span className="text-xs text-red-600 mt-1">{errors.host.message}</span>}
          </div>
          {/* {engineType !== 'snowflake' && ( */}
          {!['snowflake', 'rest_api', 'dynamics365', 'zoho_crm', 'zoho_books', 'zoho_inventory', 'sugarcrm', 'netsuite', 'suitecrm', 'defontana'].includes(engineType) && (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">{t('wizard.forms.port')}</label>
              <input
                {...register('port')}
                type="text"
                onBlur={(e) => onTrimField?.('port', e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none bg-slate-50 focus:ring-2 focus:ring-indigo-600"
              />
            </div>
          )}
        </div>
      )}

      {/* {engineType !== 'bigquery' && ( */}
      {!['bigquery', 'rest_api', 'hubspot'].includes(engineType) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {/* <label className="text-sm font-medium text-slate-700 mb-1 block">{t('wizard.forms.user')}</label> */}
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              {engineType === 'salesforce' ? t('wizard.forms.consumer_key') : ['dynamics365', 'zoho_crm', 'zoho_books', 'zoho_inventory'].includes(engineType) ? t('wizard.forms.client_id') : engineType === 'netsuite' ? t('wizard.forms.consumer_key') : t('wizard.forms.user')}
            </label>
            <input
              {...register('user')}
              autoComplete="off"
              type="text"
              onBlur={(e) => onTrimField?.('user', e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.user ? 'border-red-500' : 'border-slate-200'}`}
            />
            {errors.user && <span className="text-xs text-red-600 mt-1">{errors.user.message}</span>}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              {engineType === 'salesforce' ? t('wizard.forms.consumer_secret') : ['dynamics365', 'zoho_crm', 'zoho_books', 'zoho_inventory'].includes(engineType) ? t('wizard.forms.client_secret') : engineType === 'netsuite' ? t('wizard.forms.consumer_secret') : t('wizard.forms.password')}
            </label>
            <div className="relative">
              <input
                {...register('password')}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                className={`w-full px-4 pr-10 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.password ? 'border-red-500' : 'border-slate-200'}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-indigo-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && <span className="text-xs text-red-600 mt-1">{errors.password.message}</span>}
          </div>
        </div>
      )}

      {!['salesforce', 'hubspot'].includes(engineType) && (
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">
            {/* {engineType === 'sap_hana' ? t('wizard.forms.schema') : t('wizard.forms.database')} */}
            {engineType === 'sap_hana'
              ? t('wizard.forms.schema')
              : engineType === 'bigquery'
                ? t('wizard.forms.dataset')
                : engineType === 'rest_api'
                  ? t('wizard.forms.endpoint')
                  : engineType === 'dynamics365'
                    ? t('wizard.forms.tenant_id')
                    : ['zoho_crm', 'zoho_books', 'zoho_inventory'].includes(engineType)
                      ? t('wizard.forms.refresh_token')
                      : ['sugarcrm', 'suitecrm'].includes(engineType)
                        ? 'Client ID'
                        : engineType === 'netsuite'
                          ? 'Token Key (Token ID)'
                          : engineType === 'defontana'
                            ? (t('wizard.forms.company_code') || 'Código de Empresa')
                            : t('wizard.forms.database')}
          </label>
          <input
            {...register('database')}
            type="text"
            onBlur={(e) => onTrimField?.('database', e.target.value)}
            placeholder={engineType === 'sap_hana' ? 'TEST_ETL' : engineType === 'bigquery' ? 'mi_dataset' : engineType === 'rest_api' ? '/v2/customers' : engineType === 'dynamics365' ? '12345678-abcd-efgh-ijkl-123456789012' : ['zoho_crm', 'zoho_books', 'zoho_inventory'].includes(engineType) ? '1000.xxxxxx.xxxxxx' : ['sugarcrm', 'suitecrm'].includes(engineType) ? 'UUID del OAuth2 Client' : engineType === 'netsuite' ? 'Token Key' : engineType === 'defontana' ? 'Ej: dfchile' : ''}
            className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.database ? 'border-red-500' : 'border-slate-200'}`}
          />
          {errors.database && <span className="text-xs text-red-600 mt-1">{errors.database.message}</span>}
        </div>
      )}

      {/* BigQuery: info de acceso Service Account */}
      {engineType === 'bigquery' && (
        <div className="mt-3 p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-800 rounded-r-lg flex items-start">
          <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
          <div>
            <p className="font-bold text-sm">{t('wizard.alerts.bq_access_title')}</p>
            <p className="text-sm mt-1">{t('wizard.alerts.bq_access_desc')}</p>
            <code className="block mt-2 bg-amber-100/80 p-2 rounded text-[11px] select-all text-amber-900 border border-amber-200 font-mono">data-migrator@varanext-etl-engine.iam.gserviceaccount.com</code>
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: HubSpot — Solo Access Token */}
      {engineType === 'hubspot' && (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              {t('wizard.forms.access_token')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                {...register('password')}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className={`w-full px-4 pr-10 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.password ? 'border-red-500' : 'border-slate-200'}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-indigo-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && <span className="text-xs text-red-600 mt-1">{errors.password.message}</span>}
          </div>
          <div className="p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-800 rounded-r-lg flex items-start">
            <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
            <div>
              <p className="font-bold text-sm">{t('wizard.alerts.hs_token_title')}</p>
              <p className="text-sm mt-1">{t('wizard.alerts.hs_token_desc')}</p>
            </div>
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: Dynamics 365 — Info box de configuración Azure AD */}
      {engineType === 'dynamics365' && (
        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-800 rounded-r-lg flex items-start">
          <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
          <div>
            <p className="font-bold text-sm">{t('wizard.alerts.d365_setup_title')}</p>
            <p className="text-sm mt-1">{t('wizard.alerts.d365_setup_desc')}</p>
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: Zoho CRM — Info box de configuración Self Client */}
      {['zoho_crm', 'zoho_books', 'zoho_inventory'].includes(engineType) && (
        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-800 rounded-r-lg flex items-start">
          <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
          <div>
            <p className="font-bold text-sm">{t('wizard.alerts.zoho_setup_title')}</p>
            <p className="text-sm mt-1">{t('wizard.alerts.zoho_setup_desc')}</p>
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: REST API — Auth + Configuración Avanzada */}
      {engineType === 'rest_api' && (
        <div className="space-y-4">
          {/* ── Tipo de autenticación ────────────────────────────────── */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <label className="text-sm font-bold text-slate-700 mb-3 block">
              {t('wizard.forms.auth_type')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { value: 'none', label: t('wizard.auth.none') },
                { value: 'api_key', label: 'API Key' },
                { value: 'bearer', label: 'Bearer Token' },
                { value: 'basic', label: 'Basic Auth' },
                { value: 'oauth2_cc', label: 'OAuth 2.0' },
              ].map(opt => (
                <label key={opt.value} className={`flex items-center p-2 rounded-lg border-2 cursor-pointer transition-all text-sm ${authType === opt.value ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-medium' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>
                  <input type="radio" value={opt.value} {...register('auth_type')} className="w-3.5 h-3.5 mr-2 cursor-pointer" />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* ── Campos dinámicos según auth_type ─────────────────────── */}
          {authType === 'api_key' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    {t('wizard.forms.api_key_name')} <span className="text-red-500">*</span>
                  </label>
                  <input {...register('api_key_name')} type="text" placeholder="X-API-Key" onBlur={(e) => onTrimField?.('api_key_name', e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">{t('wizard.forms.api_key_location')}</label>
                  <select {...register('api_key_location')} className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600 bg-white">
                    <option value="header">Header</option>
                    <option value="query">Query Param</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  {t('wizard.forms.api_key_value')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input {...register('password')} type={showPassword ? "text" : "password"} autoComplete="new-password" className="w-full px-4 pr-10 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-indigo-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {authType === 'bearer' && (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Bearer Token <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input {...register('password')} type={showPassword ? "text" : "password"} autoComplete="new-password" placeholder="eyJhbGciOiJIUzI1NiIs..." className="w-full px-4 pr-10 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-indigo-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {authType === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  {t('wizard.forms.user')} <span className="text-red-500">*</span>
                </label>
                <input {...register('user')} type="text" autoComplete="off" onBlur={(e) => onTrimField?.('user', e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  {t('wizard.forms.password')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input {...register('password')} type={showPassword ? "text" : "password"} autoComplete="new-password" className="w-full px-4 pr-10 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-indigo-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {authType === 'oauth2_cc' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Client ID <span className="text-red-500">*</span>
                  </label>
                  <input {...register('user')} type="text" autoComplete="off" onBlur={(e) => onTrimField?.('user', e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Client Secret <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input {...register('password')} type={showPassword ? "text" : "password"} autoComplete="new-password" className="w-full px-4 pr-10 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-indigo-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Token URL <span className="text-red-500">*</span>
                </label>
                <input {...register('token_url')} type="text" placeholder="https://auth.example.com/oauth/token" onBlur={(e) => onTrimField?.('token_url', e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
              </div>
            </div>
          )}

          {/* ── Configuración Avanzada (colapsable) ─────────────────── */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setShowAdvancedRest(!showAdvancedRest)} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-sm font-medium text-slate-700">
              <span>{t('wizard.forms.advanced_config')}</span>
              {showAdvancedRest ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {showAdvancedRest && (
              <div className="p-4 space-y-4 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">{t('wizard.forms.pagination_type')}</label>
                    <select {...register('pagination_type')} className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600 bg-white">
                      <option value="auto">{t('wizard.pagination.auto')}</option>
                      <option value="none">{t('wizard.pagination.none')}</option>
                      <option value="offset">Offset / Limit</option>
                      <option value="page">{t('wizard.pagination.page')}</option>
                      <option value="cursor">Cursor</option>
                      <option value="link">Link Header</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">{t('wizard.forms.page_size')}</label>
                    <input {...register('page_size')} type="number" placeholder="100" className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                  </div>
                </div>

                {paginationType === 'offset' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Param offset</label>
                      <input {...register('param_offset')} placeholder="offset" className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Param limit</label>
                      <input {...register('param_limit')} placeholder="limit" className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                    </div>
                  </div>
                )}

                {paginationType === 'page' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Param page</label>
                      <input {...register('param_page')} placeholder="page" className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Param per_page</label>
                      <input {...register('param_per_page')} placeholder="per_page" className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                    </div>
                  </div>
                )}

                {paginationType === 'cursor' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">{t('wizard.forms.cursor_field')}</label>
                      <input {...register('cursor_field')} placeholder="next_cursor" className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">{t('wizard.forms.cursor_param')}</label>
                      <input {...register('cursor_param')} placeholder="cursor" className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    {t('wizard.forms.data_path')} <span className="text-slate-400 font-normal">({t('wizard.forms.data_path_hint')})</span>
                  </label>
                  <input {...register('data_path')} placeholder="data.results" onBlur={(e) => onTrimField?.('data_path', e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600" />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    {t('wizard.forms.custom_headers')} <span className="text-slate-400 font-normal">({t('wizard.forms.custom_headers_hint')})</span>
                  </label>
                  <textarea {...register('custom_headers')} rows={3} placeholder={"X-Tenant-ID: abc123\nAccept-Language: es"} className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600 font-mono text-sm" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: Campos extra Snowflake — Warehouse (obligatorio) + Schema (opcional) */}
      {engineType === 'snowflake' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              {t('wizard.forms.warehouse')} <span className="text-red-500">*</span>
            </label>
            <input
              {...register('warehouse')}
              type="text"
              onBlur={(e) => onTrimField?.('warehouse', e.target.value)}
              placeholder={t('wizard.forms.warehouse_placeholder') || 'COMPUTE_WH'}
              className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.warehouse ? 'border-red-500' : 'border-slate-200'}`}
            />
            {errors.warehouse && <span className="text-xs text-red-600 mt-1">{errors.warehouse.message}</span>}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              {t('wizard.forms.schema')} <span className="text-slate-400 font-normal">({t('common.optional')})</span>
            </label>
            <input
              {...register('schema')}
              type="text"
              onBlur={(e) => onTrimField?.('schema', e.target.value)}
              placeholder={t('wizard.forms.schema_placeholder') || 'PUBLIC'}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600"
            />
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: SugarCRM — Campo extra Client Secret */}
      {['sugarcrm', 'suitecrm'].includes(engineType) && (
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">
            Client Secret <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register('port')}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Client Secret"
              className={`w-full px-4 pr-10 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.port ? 'border-red-500' : 'border-slate-200'}`}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-indigo-600">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: NetSuite — Campo extra Token Secret */}
      {engineType === 'netsuite' && (
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">
            Token Secret <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register('port')}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Token Secret"
              className={`w-full px-4 pr-10 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.port ? 'border-red-500' : 'border-slate-200'}`}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-indigo-600">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: SuiteCRM — Info box */}
      {engineType === 'suitecrm' && (
        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-800 rounded-r-lg flex items-start">
          <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
          <div>
            <p className="font-bold text-sm">{t('wizard.alerts.suitecrm_setup_title')}</p>
            <p className="text-sm mt-1">{t('wizard.alerts.suitecrm_setup_desc')}</p>
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: SugarCRM — Info box */}
      {engineType === 'sugarcrm' && (
        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-800 rounded-r-lg flex items-start">
          <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
          <div>
            <p className="font-bold text-sm">{t('wizard.alerts.sugar_setup_title')}</p>
            <p className="text-sm mt-1">{t('wizard.alerts.sugar_setup_desc')}</p>
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: NetSuite — Info box */}
      {engineType === 'netsuite' && (
        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-800 rounded-r-lg flex items-start">
          <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
          <div>
            <p className="font-bold text-sm">{t('wizard.alerts.netsuite_setup_title')}</p>
            <p className="text-sm mt-1">{t('wizard.alerts.netsuite_setup_desc')}</p>
          </div>
        </div>
      )}

      {/* SECCIÓN 3b: Defontana — Campo extra Client Code + Info box */}
      {engineType === 'defontana' && (
        <>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              {t('wizard.forms.client_code') || 'Código de Cliente Defontana'} <span className="text-red-500">*</span>
            </label>
            <input
              {...register('port')}
              type="text"
              autoComplete="off"
              onBlur={(e) => onTrimField?.('port', e.target.value)}
              placeholder={t('wizard.forms.client_code_placeholder') || 'Código asignado por Defontana'}
              className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.port ? 'border-red-500' : 'border-slate-200'}`}
            />
            {errors.port && <span className="text-xs text-red-600 mt-1">{errors.port.message}</span>}
          </div>
          <div className="p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-800 rounded-r-lg flex items-start">
            <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
            <div>
              <p className="font-bold text-sm">{t('wizard.alerts.defontana_setup_title') || 'Configuración Defontana'}</p>
              <p className="text-sm mt-1">{t('wizard.alerts.defontana_setup_desc') || 'Ingresa el código de cliente y empresa asignados por Defontana. El ambiente "production" conecta a api.defontana.com y "sandbox" a replapi.defontana.com.'}</p>
            </div>
          </div>
        </>
      )}

      {/* SECCIÓN 4: SSH Tunnel Config (si connectionMethod = 'ssh' Y es red privada) */}
      {networkType === 'local' && connectionMethod === 'ssh' && (
        <div className="mt-6 p-5 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-2xl shadow-sm animate-in fade-in">
          <div className="flex items-start gap-3 mb-4">
            <ShieldCheck className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-indigo-900 text-sm">
                {t('wizard.connection.ssh_config') || 'Configuración SSH Tunnel'}
              </h4>
              <p className="text-xs text-indigo-700 mt-1">
                {t('wizard.connection.ssh_secure') || 'Conexión segura a través de tu servidor SSH'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* SSH Host */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                {t('wizard.forms.ssh_host') || 'SSH Host'} <span className="text-red-500">*</span>
              </label>
              <input
                {...register('ssh_host')}
                type="text"
                onBlur={(e) => onTrimField?.('ssh_host', e.target.value)}
                placeholder="192.168.1.50 o bastion.example.com"
                autoComplete="off"
                className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.ssh_host ? 'border-red-500' : 'border-slate-200'
                  }`}
              />
              {errors.ssh_host && <span className="text-xs text-red-600 mt-1">{errors.ssh_host.message}</span>}
            </div>

            {/* SSH Port & User */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  {t('wizard.forms.ssh_port') || 'SSH Port'}
                </label>
                <input
                  {...register('ssh_port')}
                  placeholder="22"
                  type="number"
                  defaultValue="22"
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  {t('wizard.forms.ssh_user') || 'SSH User'} <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('ssh_user')}
                  type="text"
                  onBlur={(e) => onTrimField?.('ssh_user', e.target.value)}
                  placeholder="ubuntu, postgres, root"
                  autoComplete="off"
                  className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.ssh_user ? 'border-red-500' : 'border-slate-200'
                    }`}
                />
                {errors.ssh_user && <span className="text-xs text-red-600 mt-1">{errors.ssh_user.message}</span>}
              </div>
            </div>

            {/* SSH Private Key */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                {t('wizard.forms.ssh_key') || 'SSH Private Key (.pem)'} <span className="text-red-500">*</span>
              </label>
              <input
                {...register('ssh_pkey_file')}
                type="file"
                accept=".pem,.key"
                className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.ssh_pkey_file ? 'border-red-500' : 'border-slate-200'
                  }`}
              />
              <p className="text-xs text-slate-500 mt-1">
                {t('wizard.connection.ssh_key_help') || 'Archivo privado (.pem) generado durante setup SSH'}
              </p>
              {errors.ssh_pkey_file && <span className="text-xs text-red-600 mt-1">{errors.ssh_pkey_file.message}</span>}
            </div>

            {/* Remote DB Host */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                {t('wizard.forms.remote_db_host') || 'Remote DB Host (dentro de tu red)'}
              </label>
              <input
                {...register('ssh_remote_bind_host')}
                type="text"
                onBlur={(e) => onTrimField?.('ssh_remote_bind_host', e.target.value)}
                placeholder="localhost o 192.168.1.51"
                defaultValue="localhost"
                autoComplete="off"
                className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-600"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t('wizard.connection.remote_db_help') || 'Generalmente "localhost" si BD está en mismo servidor'}
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-4 p-3 bg-white rounded-lg border border-indigo-100 text-xs text-slate-600">
            <p className="font-medium text-indigo-700 mb-1">
              {t('wizard.connection.ssh_benefit') || '✓ Beneficios:'}
            </p>
            <ul className="space-y-1 ml-4">
              <li>• {t('wizard.connection.ssh_no_firewall') || 'No necesitas abrir tu firewall'}</li>
              <li>• {t('wizard.connection.ssh_encrypted') || 'Conexión encriptada (SSH)'}</li>
            </ul>
          </div>

          {/* 🆕 Botón: Generar solicitud para DBA */}
          <DbaRequestButton
            engineType={engineType}
            watch={watch}
            connectionMethod="ssh"
            networkType="local"
            isTarget={isTarget}
            t={t}
          />
        </div>
      )}

      {/* SECCIÓN 5: Firewall Instructions Panel (si connectionMethod = 'firewall' Y es red privada) */}
      {networkType === 'local' && connectionMethod === 'firewall' && !isTarget && (
        <div className="mt-6 p-5 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl shadow-sm animate-in fade-in">
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-amber-900 text-sm">
                {t('wizard.connection.firewall_required') || 'Abre tu Firewall'}
              </h4>
              <p className="text-xs text-amber-700 mt-1">
                {t('wizard.connection.firewall_desc') || 'Agrega esta IP a tu whitelist'}
              </p>
            </div>
          </div>

          {/* IP Display */}
          <div className="mb-4 p-4 bg-white rounded-xl border-2 border-amber-300">
            <p className="text-xs font-bold text-amber-700 uppercase mb-2">
              {t('wizard.connection.your_ip') || 'IP de DatiaFlow'}
            </p>
            <div className="flex items-center justify-between gap-3">
              <code className="text-lg font-bold text-slate-900">{DATIAFLOW_IP}</code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(DATIAFLOW_IP);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-sm font-medium transition"
              >
                <Copy className="w-4 h-4" />
                {t('common.copy') || 'Copiar'}
              </button>
            </div>
          </div>

          {/* Quick Instructions */}
          <div className="p-3 bg-white rounded-lg border border-amber-100 text-xs text-slate-600 space-y-2">
            <p className="font-bold text-amber-700">{t('dba_request.quick_instructions') || 'Instrucciones rápidas:'}</p>
            <ol className="ml-4 space-y-1 list-decimal">
              <li>{t('dba_request.quick_step1') || 'Copia la IP arriba'}: {DATIAFLOW_IP}</li>
              <li>{t('dba_request.quick_step2') || 'Abre tu firewall (depende de tu proveedor)'}</li>
              <li>{t('dba_request.quick_step3') || 'Protocolo: TCP, Puerto según tu motor de base de datos'}</li>
              <li>{t('dba_request.quick_step4') || 'Origen'}: {DATIAFLOW_IP}/32</li>
              <li>{t('dba_request.quick_step5') || 'Espera 1-2 minutos y prueba la conexión'}</li>
            </ol>
          </div>

          {/* 🆕 Botón: Generar solicitud para DBA */}
          <DbaRequestButton
            engineType={engineType}
            watch={watch}
            connectionMethod="firewall"
            networkType="local"
            isTarget={isTarget}
            t={t}
          />
        </div>
      )}
    </div>
  );
};


// ==========================================
// 3. FORMULARIO GOOGLE SHEETS
// ==========================================
export const GoogleSheetForm = ({ register, errors, handleHostChange, isTarget, onTrimField }) => {

  const { t } = useLanguageStore();
  return (
    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4 animate-in fade-in">
      <label className="text-sm font-medium text-slate-700 mb-1 block">{t('wizard.forms.sheet_id')}</label>
      <input
        {...register('host', { onChange: handleHostChange })}
        type="text"
        onBlur={(e) => onTrimField?.('host', e.target.value)}
        placeholder="Ej: 1hZmWR99_hcpl_2Q_5eol7LtVlJCWCdbrrywssdshteQ"
        className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none font-mono text-sm ${errors.host ? 'border-red-500' : 'border-slate-200'}`}
      />

      <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
        <span className="font-semibold text-slate-800">💡 {t('common.tip')}:</span> {t('wizard.alerts.sheet_tip')}
      </div>

      <div className="mt-3 p-4 bg-blue-50/50 border border-blue-200 text-blue-800 rounded-xl flex items-start shadow-sm">
        <Server className="w-5 h-5 mr-3 shrink-0 text-blue-500" />
        <div>
          <p className="font-bold text-sm">⚡ {t('wizard.alerts.perf_tip')}</p>
          <p className="text-[13px] mt-1 leading-relaxed opacity-90">
            {t('wizard.alerts.sheet_perf_desc')}
          </p>
        </div>
      </div>

      <div className="mt-3 p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-800 rounded-r-lg flex items-start">
        <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
        <div>
          <p className="font-bold text-sm">{isTarget ? t('wizard.alerts.write_req') : t('wizard.alerts.read_req')}</p>
          <p className="text-sm mt-1">{t('wizard.alerts.share_with')} <b className="uppercase bg-amber-200 px-1 rounded text-[11px]">{isTarget ? t('wizard.alerts.editor') : t('wizard.alerts.reader')}</b> {t('wizard.alerts.to_email')}:</p>
          <code className="block mt-2 bg-amber-100/80 p-2 rounded text-[11px] select-all text-amber-900 border border-amber-200 font-mono">data-migrator@varanext-etl-engine.iam.gserviceaccount.com</code>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 🆕 DBA REQUEST BUTTON (reutilizable)
// ==========================================
const DbaRequestButton = ({ engineType, watch, connectionMethod, networkType, isTarget, t }) => {
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    const data = watch?.() || {};
    const engine = engineType || data.engineType;
    const text = generateDbaRequest({
      engineType: engine,
      engineLabel: t(`wizard.engines.${engine}`) || engine,
      host: data.host,
      port: data.port,
      database: data.database,
      user: data.user,
      connectionMethod,
      networkType,
      sshHost: data.ssh_host,
      sshPort: data.ssh_port,
      sshUser: data.ssh_user,
      isTarget: !!isTarget,
      t,
    });
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <button
      type="button"
      onClick={handleGenerate}
      className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:bg-indigo-50 rounded-xl text-sm font-medium text-slate-700 hover:text-indigo-700 transition-all"
    >
      {copied ? (
        <>
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span className="text-emerald-700">{t('dba_request.copied_msg') || '¡Copiado! Envíalo a tu DBA'}</span>
        </>
      ) : (
        <>
          <FileText className="w-4 h-4" />
          <span>{t('dba_request.generate_btn') || 'Generar solicitud para tu DBA (TI)'}</span>
          <Copy className="w-3.5 h-3.5 ml-1 text-slate-400" />
        </>
      )}
    </button>
  );
};

// ==========================================
// 4. FORMULARIO ARCHIVOS PLANOS (CSV/EXCEL)
// ==========================================
export const FlatFileForm = ({ register, errors, isTarget, engineType, showPassword, setShowPassword, watch, setValue }) => {
  const { t } = useLanguageStore(); // 🟢 Instanciamos i18n
  const isExcel = engineType === 'excel';
  const uploadMethod = watch('uploadMethod') || 'url';
  const currentPath = watch('password');
  const originalName = watch('originalFileName');

  const tempDir = import.meta.env.VITE_TEMP_FILE_DIR || '/app/exports/';
  // const isHydratedLocalFile = uploadMethod === 'local' && currentPath && currentPath.startsWith(tempDir);

  const isHydratedLocalFile = currentPath && (
    (uploadMethod === 'local' && currentPath.startsWith(tempDir)) ||
    currentPath.startsWith('/app/')   // detecta path servidor aunque uploadMethod no coincida
  );

  const isSensitiveUrl = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return lowerUrl.startsWith('ftp://') || lowerUrl.startsWith('sftp://') || url.includes('@');
  };

  const inputType = (uploadMethod === 'url' && !isHydratedLocalFile) && isSensitiveUrl(currentPath)
    ? (showPassword ? "text" : "password")
    : "text";

  React.useEffect(() => {
    if (!watch('uploadMethod')) setValue('uploadMethod', 'url');
  }, [setValue, watch]);

  return (
    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4 animate-in fade-in">

      {!isTarget && !isHydratedLocalFile && (
        <div className="flex bg-slate-100 p-1 rounded-lg w-full mb-4">
          <button type="button" onClick={() => setValue('uploadMethod', 'url')} className={`flex-1 flex items-center justify-center py-2 text-sm font-medium rounded-md transition-all ${uploadMethod === 'url' ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-500 hover:text-slate-700'}`}>
            <LinkIcon className="w-4 h-4 mr-2" /> {t('wizard.forms.remote_path')}
          </button>
          <button type="button" onClick={() => setValue('uploadMethod', 'local')} className={`flex-1 flex items-center justify-center py-2 text-sm font-medium rounded-md transition-all ${uploadMethod === 'local' ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-500 hover:text-slate-700'}`}>
            <UploadCloud className="w-4 h-4 mr-2" /> {t('wizard.forms.upload_local')}
          </button>
        </div>
      )}

      {(uploadMethod === 'url' || isTarget || isHydratedLocalFile) ? (
        <>
          <label className="text-sm font-medium text-slate-700 mb-1 block">
            {isHydratedLocalFile ? t('wizard.forms.file_selected') : `${t('wizard.forms.path_to')} ${isTarget ? t('wizard.forms.target') : t('wizard.forms.file')}, URL, FTP o SFTP`}
            {isTarget && <span className="text-slate-400 font-normal"> ({t('common.optional')})</span>}
          </label>
          <div className="relative">
            {isHydratedLocalFile ? (
              // Display-only: muestra solo el nombre sin la ruta del servidor
              // RHF sigue manteniendo el path real en su estado interno
              <input
                type="text"
                readOnly
                value={originalName || currentPath.split('/').pop()}
                className="w-full pl-4 pr-10 py-2 rounded-lg border border-slate-200 bg-emerald-50 text-emerald-800 font-semibold cursor-default outline-none"
              />
            ) : (
              <input
                type={inputType}
                {...register('password')}
                placeholder={isTarget
                  ? t('wizard.forms.placeholder_ftp_target')
                  : t('wizard.forms.placeholder_ftp_source')}
                className={`w-full pl-4 pr-10 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-600 outline-none ${errors.password ? 'border-red-500' : 'border-slate-200'} bg-white`}
              />
            )}
            {(!isHydratedLocalFile && isSensitiveUrl(currentPath)) && (
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-indigo-600">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            )}
            {isHydratedLocalFile && (
              <button type="button" onClick={() => { setValue('password', ''); setValue('originalFileName', ''); setValue('uploadMethod', 'local'); }} className="absolute inset-y-0 right-0 flex items-center pr-3 text-red-500 hover:text-red-700" title={t('common.buttons.change')}>
                <XCircle className="w-5 h-5" />
              </button>
            )}
          </div>
          <div className="mt-2 p-2.5 bg-emerald-50 border border-emerald-100 rounded-md flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-xs font-medium text-emerald-800 leading-relaxed">
              {t('wizard.alerts.secure_path')}
              {uploadMethod === 'url' && (
                <span className="block mt-1 font-normal text-slate-600">{t('wizard.alerts.ftp_format')} <br /><code className="bg-emerald-100 px-1 py-0.5 rounded text-[11px] font-mono">protocol://user:password@host.com/path/file.csv</code></span>
              )}
            </p>
          </div>
        </>
      ) : (
        <>
          <label className="text-sm font-medium text-slate-700 mb-1 block">{t('wizard.forms.select_file')} {engineType.toUpperCase()}</label>
          <div className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center transition-colors cursor-pointer relative ${errors.localFile ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'}`}>
            <input type="file" {...register('localFile')} accept={engineType === 'csv' ? '.csv' : engineType === 'excel' ? '.xlsx,.xls' : engineType === 'json' ? '.json,.ndjson' : '.dbf'} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
            <UploadCloud className={`w-10 h-10 mb-2 ${errors.localFile ? 'text-red-400' : 'text-slate-400'}`} />
            <p className="text-sm font-medium text-slate-700">{t('wizard.forms.drag_drop')}</p>
            <p className="text-xs text-slate-500 mt-1">{t('wizard.forms.supports')}: {engineType.toUpperCase()}</p>
          </div>

          {watch('localFile')?.[0] && (
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between animate-in fade-in">
              <p className="text-sm text-emerald-700 font-medium flex items-center truncate pr-4">
                <CheckCircle2 className="w-5 h-5 mr-2 shrink-0 text-emerald-500" />
                <span className="truncate">{watch('localFile')[0].name}</span>
              </p>
              <button type="button" onClick={(e) => { e.preventDefault(); setValue('localFile', null); }} className="p-1.5 text-emerald-600 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title={t('common.buttons.remove')}>
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          )}
        </>
      )}

      {isExcel && !isTarget && (
        <div className="mt-3 p-4 bg-blue-50/50 border border-blue-200 text-blue-800 rounded-xl flex items-start shadow-sm">
          <Server className="w-5 h-5 mr-3 shrink-0 text-blue-500" />
          <div>
            <p className="font-bold text-sm">⚡ {t('wizard.alerts.perf_tip')}</p>
            <p className="text-[13px] mt-1 leading-relaxed opacity-90">
              {t('wizard.alerts.excel_warning')}
            </p>
          </div>
        </div>
      )}

      {isTarget && (
        <>
          <div className="mt-3 flex items-start gap-3 bg-slate-50 p-3 border border-slate-200 rounded-lg">
            <input type="checkbox" id="includeHeaders" {...register('includeHeaders')} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer" />
            <div>
              <label htmlFor="includeHeaders" className="text-sm font-semibold text-slate-800 cursor-pointer block">
                {t('wizard.forms.include_headers')}
              </label>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                {t('wizard.forms.headers_desc')}
              </p>
            </div>
          </div>

          <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 flex items-start">
            <FileSpreadsheet className="w-4 h-4 mr-2 shrink-0 text-slate-400 mt-0.5" />
            <span><strong className="text-slate-800">{t('wizard.alerts.leave_blank')}</strong> {t('wizard.alerts.blank_desc')}</span>
          </div>
        </>
      )}
    </div>
  );
};